import { model as i18n } from '!/i18n'
import { register } from '!/repository'
import { getNextPage } from '@/effector'
import { checkIsSearchQueryValid } from '@/helpers'
import {
  api,
  GlobalSearchMediaTypes,
  type TvChannelUpdateObj,
  type TvShowUpdateObj,
  type VodUpdateObj,
} from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { not, or } from 'patronum'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import {
  extendMediaItemByLabelPrice,
  extendMediaItemByLabelPriceTvChannel,
} from '~/shared/tools'
import { initRecommendedVodsPage, searchInit } from '../root/model'
import { model as search } from '../shared/searchField'

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

// Constants

const INIT_GRID_ELEMENTS_COUNT = 11

// Events

export const left = createEvent()
const updateVod = createEvent<VodUpdateObj>()
const updateTvChannel = createEvent<TvChannelUpdateObj>()
const updateTvShow = createEvent<TvShowUpdateObj>()

// Effects

const getSearchEventsFx = attach({
  effect: api.globalSearch.loadSearchEventsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const getSearchTvChannelsFx = attach({
  effect: api.globalSearch.loadSearchTvChannelsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const getSearchTvShowsFx = attach({
  effect: api.globalSearch.loadSearchTvShowsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const getSearchVodsFx = attach({
  effect: api.globalSearch.loadSearchVodsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const $content = combine(
  api.globalSearch.$searchResults,
  ssePurchasesModel.$liveEvent,
  ssePurchasesModel.$channel,
  ssePurchasesModel.$tvShow,
  ssePurchasesModel.$vod,
  (data, adjustEvent, adjustChannel, adjustShow, adjustVod) => ({
    liveEvents: data?.liveEvents?.map((liveEvent) => adjustEvent(liveEvent)),
    tvChannels: data?.tvChannels?.map((liveEvent) => adjustChannel(liveEvent)),
    tvShows: data?.tvShows?.map((liveEvent) => adjustShow(liveEvent)),
    vods: data?.vods?.map((liveEvent) => adjustVod(liveEvent)),
  })
)

const $contentFormatted = combine($content, i18n.$t, (content, { t }) => [
  {
    contentType: GlobalSearchMediaTypes.LIVE_EVENT,
    title: t('event_other'),
    list: content?.liveEvents?.map((liveEvent) =>
      extendMediaItemByLabelPrice(liveEvent)
    ),
  },
  {
    contentType: GlobalSearchMediaTypes.TV_CHANNEL,
    title: t('live_channels'),
    list: content?.tvChannels?.map((tvChannel) =>
      extendMediaItemByLabelPriceTvChannel(tvChannel)
    ),
  },
  {
    contentType: GlobalSearchMediaTypes.TV_SHOW,
    title: t('series'),
    list: content?.tvShows?.map((tvShow) =>
      extendMediaItemByLabelPrice(tvShow)
    ),
  },
  {
    contentType: GlobalSearchMediaTypes.VOD,
    title: t('movies'),
    list: content?.vods?.map((vod) => extendMediaItemByLabelPrice(vod)),
  },
])

const $hasResults = $content.map((content) =>
  Object.values(content).some((value) => value?.length)
)

const $isRequestsPending = api.globalSearch.getSearchFx.pending

const $noResultsMessage = createStore('')
sample({
  clock: $content,
  source: {
    value: search.$searchValue,
    hasResults: $hasResults,
    isRequestsPending: $isRequestsPending,
    i18n: i18n.$t,
  },
  filter: ({ hasResults, isRequestsPending }) =>
    !hasResults && !isRequestsPending,
  fn: ({ value, i18n: { t } }) =>
    t('search_no_results', {
      request: value,
    }),
  target: $noResultsMessage,
})

// Connections

/* Reset on searchInit is necessary so that old $searchResults should not be saved */
sample({
  clock: searchInit,
  fn: () => null,
  target: api.globalSearch.$searchResults,
})

sample({
  clock: searchInit,
  filter: ({ query }) =>
    query.has('q') && checkIsSearchQueryValid(query.get('q') || ''),
  fn: ({ query }) => ({
    q: query.get('q') || '',
    count: INIT_GRID_ELEMENTS_COUNT,
  }),
  target: api.globalSearch.getSearchFx,
})

/********** Skeleton logics starts ***********/

const $isSearchByContentTypePending = or(
  api.globalSearch.loadSearchEventsFx.pending,
  api.globalSearch.loadSearchTvChannelsFx.pending,
  api.globalSearch.loadSearchTvShowsFx.pending,
  api.globalSearch.loadSearchVodsFx.pending
)

sample({
  clock: api.globalSearch.getSearchFx.pending,
  filter: not($isSearchByContentTypePending),
  target: setContentLoading,
})

/********** Skeleton logics ends ***********/

// Add to favorite

sample({
  clock: updateVod,
  fn: (params) => ({ ...params, favorite: !params.favorite }),
  target: api.vods.updateVodFx,
})

sample({
  clock: updateTvChannel,
  fn: (params) => ({ ...params, favorite: !params.favorite }),
  target: api.tv.updateTvChannelFx,
})

sample({
  clock: updateTvShow,
  fn: (params) => ({ ...params, favorite: !params.favorite }),
  target: api.tvshows.updateTvShowFx,
})

sample({
  clock: $noResultsMessage,
  source: searchInit,
  target: initRecommendedVodsPage,
})

// Load more content

const loadMoreEvents = getNextPage(
  api.globalSearch.$searchEventsPageInfo,
  getSearchEventsFx
)

const loadMoreTvChannels = getNextPage(
  api.globalSearch.$searchTvChannelsPageInfo,
  getSearchTvChannelsFx
)

const loadMoreTvShows = getNextPage(
  api.globalSearch.$searchTvShowsPageInfo,
  getSearchTvShowsFx
)

const loadMoreVods = getNextPage(
  api.globalSearch.$searchVodsPageInfo,
  getSearchVodsFx
)

register($contentFormatted, '$search/contentFormatted')
register($isRequestsPending, '$search/isRequestsPending')
register($hasResults, '$search/hasResults')
register($noResultsMessage, '$search/noResultsMessage')
register(api.globalSearch.$searchVodsPageInfo, '$search/vodsPageInfo')
register(api.globalSearch.$searchTvShowsPageInfo, '$search/allTvShowsPageInfo')
register(
  api.globalSearch.$searchTvChannelsPageInfo,
  '$search/allTvChannelsPageInfo'
)
register(api.globalSearch.$searchEventsPageInfo, '$search/searchEventsPageInfo')
register(updateVod, 'search/updateVod')
register(updateTvChannel, 'search/updateTvChannel')
register(updateTvShow, 'search/updateTvShow')
register(loadMoreVods, 'search/loadMoreVods')
register(loadMoreTvShows, 'search/loadMoreTvShows')
register(loadMoreTvChannels, 'search/loadMoreTvChannels')
register(loadMoreEvents, 'search/loadMoreEvents')
